// frontend/src/components/shared/PropertyManagersPanel.js
// Used in both landlord portal and employee landlord portfolio page
// Props: landlordId, properties, isEmployee (bool)

import { useState, useEffect } from 'react';
import api from '../../utils/api';

const lbl = { fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 };
const inp = { width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#1e293b', outline: 'none', background: 'white', boxSizing: 'border-box' };
const EMPTY = { first_name: '', last_name: '', email: '', phone: '', property_id: '' };

export default function PropertyManagersPanel({ landlordId, properties = [], isEmployee = false }) {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);

  const load = () => {
    const url = isEmployee ? '/employee/property-managers?landlord_id=' + landlordId : '/landlord/property-managers';
    api.get(url)
      .then(res => setManagers(res.data.property_managers || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [landlordId]);

  const handleSave = async () => {
    if (!form.first_name || !form.last_name || !form.email) { setError('First name, last name, and email are required'); return; }
    setSaving(true); setError('');
    try {
      const url = isEmployee ? '/employee/property-managers' : '/landlord/property-managers';
      const payload = isEmployee ? { ...form, landlord_id: landlordId } : form;
      const res = await api.post(url, payload);
      setCreated(res.data);
      setForm(EMPTY);
      load();
    } catch (err) { setError(err.response?.data?.error || 'Could not create property manager'); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (pm) => {
    const url = isEmployee ? '/employee/property-managers/' + pm.id : '/landlord/property-managers/' + pm.id;
    try {
      await api.patch(url, { status: pm.status === 'active' ? 'inactive' : 'active' });
      load();
    } catch {}
  };

  return (
    <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0', padding: '1.5rem', marginTop: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>👥 Property Managers</h3>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Sub-users who can access specific properties</div>
        </div>
        <button onClick={() => { setShowForm(!showForm); setCreated(null); setError(''); }} style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          {showForm ? 'Cancel' : '+ Add Manager'}
        </button>
      </div>

      {/* Add form */}
      {showForm && !created && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>New Property Manager</div>
          {error && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 7, padding: '8px 12px', color: '#dc2626', fontSize: 12, marginBottom: 12 }}>{error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={lbl}>First Name *</label>
              <input value={form.first_name} onChange={e => setForm(p => ({ ...p, first_name: e.target.value }))} style={inp} placeholder="Jane" />
            </div>
            <div>
              <label style={lbl}>Last Name *</label>
              <input value={form.last_name} onChange={e => setForm(p => ({ ...p, last_name: e.target.value }))} style={inp} placeholder="Doe" />
            </div>
            <div>
              <label style={lbl}>Email *</label>
              <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} style={inp} placeholder="jane@smithproperties.com" />
            </div>
            <div>
              <label style={lbl}>Phone</label>
              <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} style={inp} placeholder="212-555-1234" />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={lbl}>Assign to Property</label>
              <select value={form.property_id} onChange={e => setForm(p => ({ ...p, property_id: e.target.value }))} style={inp}>
                <option value="">All properties (no restriction)</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name} — {p.address}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button onClick={() => { setShowForm(false); setForm(EMPTY); setError(''); }} style={{ background: '#f1f5f9', border: 'none', borderRadius: 7, padding: '8px 16px', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 7, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Creating...' : 'Create & Get Credentials'}
            </button>
          </div>
        </div>
      )}

      {/* Success / credentials display */}
      {created && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, color: '#16a34a', marginBottom: 8 }}>✅ Property Manager Created!</div>
          <div style={{ fontSize: 13, marginBottom: 4 }}><strong>Email:</strong> {created.property_manager?.email}</div>
          <div style={{ fontSize: 13, marginBottom: 8 }}><strong>Temp Password:</strong> <code style={{ background: '#dcfce7', padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>{created.temp_password}</code></div>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>Login URL: <strong>https://bulknet-frontend.vercel.app/landlord/login</strong></div>
          <button onClick={() => { setCreated(null); setShowForm(false); }} style={{ background: '#16a34a', color: 'white', border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Done</button>
        </div>
      )}

      {/* Managers list */}
      {loading ? (
        <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Loading...</div>
      ) : managers.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No property managers yet. Add one above.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              {['Name', 'Email', 'Phone', 'Assigned Property', 'Status', ''].map(h => (
                <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {managers.map(pm => (
              <tr key={pm.id} style={{ borderTop: '1px solid #f1f5f9', opacity: pm.status === 'inactive' ? 0.5 : 1 }}>
                <td style={{ padding: '10px 12px', fontWeight: 600 }}>{pm.first_name} {pm.last_name}</td>
                <td style={{ padding: '10px 12px', color: '#64748b' }}>{pm.email}</td>
                <td style={{ padding: '10px 12px', color: '#64748b' }}>{pm.phone || '—'}</td>
                <td style={{ padding: '10px 12px', color: '#475569' }}>{pm.property_name || <span style={{ color: '#94a3b8' }}>All properties</span>}</td>
                <td style={{ padding: '10px 12px' }}>
                  <span style={{ background: pm.status === 'active' ? '#dcfce7' : '#f1f5f9', color: pm.status === 'active' ? '#16a34a' : '#94a3b8', borderRadius: 100, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>
                    {pm.status}
                  </span>
                </td>
                <td style={{ padding: '10px 12px' }}>
                  <button onClick={() => toggleStatus(pm)} style={{ background: pm.status === 'active' ? '#fef2f2' : '#f0fdf4', color: pm.status === 'active' ? '#dc2626' : '#16a34a', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                    {pm.status === 'active' ? 'Deactivate' : 'Reactivate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
